import { CrudForm } from '../shared/CrudPage';
import { enumEntries, expenseService } from '@kss/api';
import type { Expense, ExpenseCreate, ExpenseFilter } from '@kss/api';
import { loadStoreOptions, loadAccountOptions } from '../shared/formLoaders';

/**
 * Expense form — wired to the live schema verified 2026-05-05.
 *
 * Live schema: expenseNumber, expenseDate, categoryAccountId (FK to
 * Account, NOT free-text `category`), paymentMode (int enum), amount,
 * taxAmount, description, vendor, receiptUrl, storeId.
 *
 * Fixes vs the legacy form:
 *   - `category` (free-text) → `categoryAccountId` (Account FK).
 *   - `paymentMethod` (string) → `paymentMode` (int enum: 1=Cash,
 *     2=Card, 3=UPI, 4=NetBanking, 5=Cheque, 6=Wallet, 7=Other).
 *   - Dropped `accountId` (renamed `categoryAccountId`), `reference`
 *     (not on schema), `isActive` (no per-row toggle).
 *   - Added `expenseNumber` (required) and `taxAmount`.
 */

/* Single source of truth — see enums.ts ExpensePaymentModeLabels (aliases PaymentMode). */
const MODE_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('ExpensePaymentMode').map((e) => [e.label, e.value]),
);
const LABEL_BY_MODE: Record<number, string> = Object.fromEntries(
  Object.entries(MODE_BY_LABEL).map(([k, v]) => [v, k]),
);

type F = {
  expenseNumber: string;
  expenseDate: string;
  amount: number;
  taxAmount: number;
  categoryAccountId: string;
  vendor: string;
  paymentMode: string;
  description: string;
  storeId: string;
};
const today = new Date().toISOString().slice(0, 10);
const empty: F = {
  expenseNumber: '',
  expenseDate: today,
  amount: 0,
  taxAmount: 0,
  categoryAccountId: '',
  vendor: '',
  paymentMode: 'Cash',
  description: '',
  storeId: '',
};

export default function NewExpense() {
  return (
    <CrudForm<Expense, ExpenseCreate, ExpenseFilter, F>
      title="expense"
      service={expenseService}
      idKey="expenseId"
      basePath="/panel/expenses"
      emptyForm={empty}
      fields={[
        { key: 'expenseNumber', label: 'Expense number', required: true, placeholder: 'EXP-1001' },
        { key: 'expenseDate', label: 'Expense date', type: 'date', required: true },
        { key: 'amount', label: 'Amount (₹)', type: 'number', required: true },
        { key: 'taxAmount', label: 'Tax (₹)', type: 'number' },
        { key: 'categoryAccountId', label: 'Category account', type: 'search', loadOptions: loadAccountOptions, placeholder: 'Pick an account' },
        { key: 'vendor', label: 'Vendor', placeholder: 'Vendor name' },
        { key: 'paymentMode', label: 'Payment method', type: 'select', options: Object.keys(MODE_BY_LABEL).map((v) => ({ value: v, label: v })) },
        { key: 'storeId', label: 'Store', type: 'search', loadOptions: loadStoreOptions, placeholder: 'Select a store' },
        { key: 'description', label: 'Description', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        expenseNumber: r.expenseNumber ?? '',
        expenseDate: r.expenseDate?.slice(0, 10) ?? today,
        amount: Number(r.amount ?? 0),
        taxAmount: Number(r.taxAmount ?? 0),
        // Read either the live `categoryAccountId` or the legacy
        // `accountId` so previously-saved rows still resolve.
        categoryAccountId: r.categoryAccountId ?? r.accountId ?? '',
        vendor: r.vendor ?? '',
        paymentMode:
          typeof r.paymentMode === 'number'
            ? LABEL_BY_MODE[r.paymentMode] ?? 'Cash'
            : String(r.paymentMode ?? r.paymentMethod ?? 'Cash'),
        description: r.description ?? '',
        storeId: r.storeId ?? '',
      })}
      toEntity={(f, id) => ({
        expenseId: id,
        expenseNumber: f.expenseNumber,
        expenseDate: f.expenseDate || undefined,
        amount: f.amount,
        taxAmount: f.taxAmount || undefined,
        categoryAccountId: f.categoryAccountId || undefined,
        vendor: f.vendor || undefined,
        paymentMode: MODE_BY_LABEL[f.paymentMode] ?? 1,
        description: f.description || undefined,
        storeId: f.storeId || undefined,
      })}
      validate={(f) =>
        !f.expenseNumber.trim()
          ? 'Expense number is required'
          : f.amount <= 0
          ? 'Amount must be > 0'
          : null
      }
    />
  );
}
